'use client';

// Hard lockout for owners whose Powston subscription has ended. Mounted once
// at the layout level so it covers every customer data page — current and
// future — without per-page wiring. When the current user is locked out
// (see useSubscriptionAccess) every page except the allow-list below is
// replaced by the SubscriptionCancelled screen.
//
// The allow-list is the set of pages a locked-out user still needs:
//   - /profile   — where they resubscribe
//   - /login, /signup, /passkey — auth (and where UserProvider renders without a
//                       context, so we can't even evaluate access)
//   - /controller_health, /system-status — public-ish status pages
//   - /terms — legal, should never be blocked
// The "request help" path (the Send feedback button) lives in the NavBar,
// which the SubscriptionCancelled screen itself renders, so it stays reachable
// everywhere.
//
// Only staff are exempt (handled in useSubscriptionAccess). Non-owners are
// exempt in practice only because they have no terminated status of their
// own, and bundled-installer customers are NOT exempt: the lockout is
// status-only and synchronous by design, so it never waits on /api/sites and
// never flashes data it is about to hide. A bundled owner only reaches
// cancelled/transferred by going through the cancel-service path, so the
// lockout and its reactivation route are the right outcome for them — and
// resolveBillingOffer deliberately keeps that route on /profile rather than
// showing them the "included with your installation" note.

import { usePathname } from 'next/navigation';
import { useSubscriptionAccess } from '@/app/lib/subscriptionAccess';
import SubscriptionCancelled from '@/app/components/SubscriptionCancelled';

const ALLOWED_PREFIXES = [
  '/login', '/signup', '/passkey', '/profile', '/controller_health', '/system-status', '/terms',
];

function isAllowedPath(pathName: string | null): boolean {
  if (!pathName) return true;
  return ALLOWED_PREFIXES.some(p => pathName === p || pathName.startsWith(p + '/'));
}

const SubscriptionGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pathName = usePathname();
  const access = useSubscriptionAccess();

  // Always let the allow-listed pages through, regardless of access state, so
  // a locked-out user can still resubscribe and ask for help.
  if (isAllowedPath(pathName)) return <>{children}</>;

  if (access === 'locked') return <SubscriptionCancelled />;

  return <>{children}</>;
};

export default SubscriptionGate;
